import {SearchBarAutoCompleteItem} from "@/auto-complete-searchbar/interfaces/ISearchBarAutoComplete";
import {AUTO_COMPLETE_SECTION} from "@/auto-complete-searchbar/interfaces";
import {ContestConfigMeta, SalesConfig} from "@/core/http/resources/contest/ContestResource";
import {Act} from "@/backing/interfaces/backing";

/**
 * Sales config service.
 * @constructor
 */
const SalesConfigService = () => {
    /**
     * Format the search bar autocomplete data to get a sales config item.
     * @param data
     */
    const formatSearchbarAutoCompleteData = (data: SearchBarAutoCompleteItem[]) => {
        const fieldMap: Record<string, "laboratory_ids" | "product_ids" | "range_ids"> = {
            [AUTO_COMPLETE_SECTION.LABORATORY]: "laboratory_ids",
            [AUTO_COMPLETE_SECTION.PRODUCT]: "product_ids",
            [AUTO_COMPLETE_SECTION.RANGES]: "range_ids",
        };

        const formattedData: { laboratory_ids?: number[]; product_ids?: number[]; range_ids?: number[]; } = {};

        data.forEach(item => {
            const field = fieldMap[item.type];
            if (!field) {
                return;
            }

            if (field in formattedData) {
                formattedData[field]?.push(item.id);
            } else {
                formattedData[field] = [item.id];
            }
        });

        return formattedData;
    };

    /**
     * Whether the act code is billed at several prices (e.g. RVA at 7,50 € and 9,60 €),
     * sales only telling them apart by their unit price.
     */
    const hasPricedVariants = (act: Act, acts: Act[]): boolean => {
        return acts.filter(other => other.code === act.code).length > 1;
    };

    /**
     * Get the prestation key of an act: its code, or its code and price when the
     * code is shared by several acts (e.g. "RVA:7.50").
     */
    const prestationKey = (act: Act, acts: Act[]): string => {
        return hasPricedVariants(act, acts) ? `${act.code}:${Number(act.price).toFixed(2)}` : act.code;
    };

    /**
     * Get the prestation keys of the selected acts, a code whose variants are all
     * selected being sent bare so that it counts every price.
     */
    const formatPrestations = (selected: Act[], acts: Act[]): string[] => {
        return selected.map(act => {
            const variants = acts.filter(other => other.code === act.code);
            const allSelected = variants.every(variant => selected.some(item => item.id === variant.id));

            return allSelected ? act.code : prestationKey(act, acts);
        });
    };

    /**
     * Get the acts matching the configured prestation keys, a bare code selecting
     * every price of that code.
     */
    const actsFromPrestations = (prestations: string[] | undefined, acts: Act[]): Act[] => {
        return acts.filter(act => prestations?.includes(act.code) || prestations?.includes(prestationKey(act, acts)));
    };

    /**
     * Get the configured keys matching no listed act (an act no longer listed, a price
     * no longer billed, or a list not loaded yet): they cannot be shown, and dropping
     * them on save would widen the contest to every sale.
     */
    const unknownPrestations = (prestations: string[], acts: Act[]): string[] => {
        return prestations.filter(key => !acts.some(act => key === act.code || key === prestationKey(act, acts)));
    };

    /**
     * Format a list of search bar autocomplete item to get a sales config item.
     */
    const formatConfig = (items: {
        laboratory_ids?: number[];
        product_ids?: number[];
        range_ids?: number[];
    }, objective: number, prestations: Act[], acts: Act[], configuredPrestations: string[] = []): SalesConfig => {
        const config: SalesConfig = {
            ...items,
            group_by: "operator",
            prestations: [...new Set([
                ...unknownPrestations(configuredPrestations, acts),
                ...formatPrestations(prestations, acts),
            ])],
        };

        if (objective > 0) {
            config.objective = {
                target: objective,
                type: "collective"
            };
        }

        return config;
    };

        const getSearchBarAutoCompleteItemsFromConfig = (config: ContestConfigMeta) => {
        const items: SearchBarAutoCompleteItem[] = [];

        if (config?.laboratories) {
            config.laboratories.forEach(laboratory => {
                items.push({
                    id: laboratory.id,
                    type: AUTO_COMPLETE_SECTION.LABORATORY,
                    name: laboratory.name,
                    cip: '',
                    meta: null,
                });
            });
        }

        if (config?.products) {
            config.products.forEach(product => {
                items.push({
                    id: product.id,
                    type: AUTO_COMPLETE_SECTION.PRODUCT,
                    name: product.name  ,
                    cip: product.code,
                    meta: null,
                });
            });
        }

        if (config?.ranges) {
            config.ranges.forEach(range => {
                items.push({
                    id: range.id,
                    type: AUTO_COMPLETE_SECTION.RANGES,
                    name: range.name,
                    cip: '',
                    meta: null,
                });
            });
        }

        return items;
    };

    return {
        formatSearchbarAutoCompleteData,
        hasPricedVariants,
        actsFromPrestations,
        formatConfig,
        getSearchBarAutoCompleteItemsFromConfig
    };
};

export default SalesConfigService;
